import { FC } from 'react'
import { DashboardCardProps, DashboardSingleClientPlan } from './types.d'
import { Button, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const UNLIMITED_THRESHOLD = 9999
const PLAN_ORDER: Record<string, number> = { free: 0, standard: 1, premium: 2 }

function getCannotDowngradeReason(
    planLabel: string,
    usage: { modulesCount: number; pagesCount: number },
    plan: DashboardSingleClientPlan
): string {
    const parts: string[] = []
    const maxModules = plan.max_modules ?? UNLIMITED_THRESHOLD
    const maxPages = plan.max_pages ?? UNLIMITED_THRESHOLD
    if (maxModules < UNLIMITED_THRESHOLD && usage.modulesCount > maxModules) {
        parts.push(
            `you're using ${usage.modulesCount} modules and ${planLabel} allows ${maxModules}`
        )
    }
    if (maxPages < UNLIMITED_THRESHOLD && usage.pagesCount > maxPages) {
        parts.push(
            `you're using ${usage.pagesCount} pages and ${planLabel} allows ${maxPages}`
        )
    }
    if (parts.length === 0) return ''
    return parts.join('; ') + '.'
}

const Plan: FC<DashboardCardProps> = ({ widgetData }) => {
    const currentPlan = (
        Object.values(widgetData.plans) as DashboardSingleClientPlan[]
    ).filter((p) => p.active === true)?.[0] as DashboardSingleClientPlan

    const usage = widgetData.usage ?? { modulesCount: 0, pagesCount: 0 }
    const currentLevel = PLAN_ORDER[Object.keys(widgetData.plans).find((k) => widgetData.plans[k].active) ?? ''] ?? 0

    const upgradeUrl = process.env.REACT_APP_UPGRADE_PLAN_URL || 'https://samirify.com/pricing'

    return (
        <div className="stat-card">
            <div
                className="stat-card__accent"
                style={{ background: 'linear-gradient(90deg, #0a63ea, #3b82f6)' }}
            />
            <div className="stat-card__header">
                <div>
                    <div className="stat-card__label text-primary">Plan</div>
                    <div className="stat-card__value text-primary">
                        {currentPlan.label}
                    </div>
                </div>
                <div
                    className="stat-card__icon"
                    style={{ background: 'linear-gradient(135deg, #0a63ea, #083bb0)' }}
                >
                    <FontAwesomeIcon icon={['fas', 'hand-holding-dollar']} />
                </div>
            </div>
            <div className="stat-card__action">
                <ButtonGroup className="w-100">
                    {Object.keys(widgetData.plans).map((planCode: string) => {
                        const plan: DashboardSingleClientPlan =
                            widgetData.plans[planCode]
                        const planLevel = PLAN_ORDER[planCode] ?? 0
                        const isHigher = planLevel > currentLevel
                        const isLower = planLevel < currentLevel
                        const cannotDowngradeReason =
                            !plan.active &&
                            typeof plan.max_modules === 'number' &&
                            typeof plan.max_pages === 'number' &&
                            getCannotDowngradeReason(plan.label, usage, plan)

                        const tooltipContent = plan.active
                            ? null
                            : isHigher
                              ? `Upgrade to ${plan.label}`
                              : isLower
                                ? cannotDowngradeReason
                                  ? null
                                  : `Downgrade to ${plan.label}`
                                : null

                        const overlay =
                            cannotDowngradeReason ? (
                                <Tooltip id={`plan-tooltip-${planCode}`} className="plan-downgrade-tooltip">
                                    <span className="d-block">Downgrade to {plan.label}.</span>
                                    <span className="plan-tooltip__prominent">
                                        <FontAwesomeIcon icon={['fas', 'circle-exclamation']} className="plan-tooltip__icon" />
                                        You cannot downgrade:
                                    </span>
                                    <span className="d-block">{cannotDowngradeReason}</span>
                                </Tooltip>
                            ) : (
                                <Tooltip id={`plan-tooltip-${planCode}`}>
                                    {tooltipContent}
                                </Tooltip>
                            )

                        if (plan.active) {
                            return (
                                <Button
                                    key={planCode}
                                    variant="outline-primary"
                                    size="sm"
                                    disabled
                                >
                                    {plan.label}
                                </Button>
                            )
                        }

                        if (cannotDowngradeReason) {
                            return (
                                <OverlayTrigger
                                    key={planCode}
                                    trigger={['hover', 'focus']}
                                    placement="bottom"
                                    overlay={overlay}
                                >
                                    <span
                                        className="btn btn-outline-primary btn-sm plan-cannot-downgrade-btn"
                                        role="button"
                                        aria-disabled="true"
                                        tabIndex={-1}
                                        onClick={(e) => e.preventDefault()}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onFocus={(e) => (e.target as HTMLElement).blur()}
                                    >
                                        {plan.label}
                                    </span>
                                </OverlayTrigger>
                            )
                        }

                        return (
                            <OverlayTrigger
                                key={planCode}
                                trigger={['hover', 'focus']}
                                placement="bottom"
                                overlay={overlay}
                            >
                                <a
                                    href={upgradeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary btn-sm"
                                >
                                    {plan.label}
                                </a>
                            </OverlayTrigger>
                        )
                    })}
                </ButtonGroup>
            </div>
        </div>
    )
}

export default Plan
